import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, FileText, Loader, Upload } from 'lucide-react';
import { validateLoanDataForm } from '../../components/LoanDataForm';
import { StatusDot } from '../../components/ui/StatusDot';
import { JobType } from '../../graphql/types';
import { setDraftStagedFiles } from '../draftHandoff';
import { useDraftJob } from '../useDraftJob';
import { ensureLoan, loadIntakeData, saveLoanFields, uploadDocument } from './intakeBAData';
import {
  ACCEPTED_FORMATS_TEXT,
  ACCEPT_ATTRIBUTE,
  AUTOSAVE_DELAY_MS,
  BA_FIELD_GROUPS,
  buildChecklist,
  countMissing,
  emptyForm,
  formsEqual,
  getExtractAction,
  pickExtras,
  saveIntakeRecord,
  saveStatusLabel,
  toLoanDataFormState,
  uniqueFileName,
  validateIntakeFile,
} from './intakeBAModel';
import type { BAFieldKey, BAFormState, SaveStatus } from './intakeBAModel';

/**
 * DraftIntakeBA: the Intake Mode of a Borrower Analysis loan that has not been
 * created yet (prototype `baDraft`).
 *
 * It is the same screen as `IntakeBA`: the empty three-group loan form with the
 * save indicator, the drop zone, the checklist with every row offering "Upload",
 * and the disabled action "Extract 0 files — add documents first". There is no
 * create form. Uploads and the loan need a real job id, so the job is created
 * on the first real action:
 *
 * - a field edit that passes validation (the borrower name is filled in),
 *   after the same short pause the saved form uses;
 * - the first file added, from the drop zone or a checklist row.
 *
 * The job gets a default name and the borrower name when it is known. The loan
 * is then saved with everything entered so far, the files are uploaded, and the
 * Workspace replaces the URL with the new loan's Intake, which shows the same
 * values. If any step fails the entries stay on screen with an inline error and
 * Retry; a retry reuses a job that was already created.
 *
 * Requirements: 2.3, 3.5, 5.1, 5.2, 5.3, 5.4.
 */
interface QueuedFile {
  id: string;
  /** Name the file is stored under (unique within the draft). */
  name: string;
  size: number;
  /** Checklist row the file was added for, when added from "Upload". */
  slot?: string;
  status: 'queued' | 'uploading' | 'completed' | 'error';
  error?: string;
}

interface Rejection {
  name: string;
  reason: string;
}

type Stage = 'create' | 'save' | 'upload';

const CARD = 'rounded-xl border border-gray-200 bg-white';

// Same resting colour as IntakeBA: white on #A34DF5 is 4.3:1, under the 4.5:1 text rule.
const EXTRACT_BUTTON =
  'inline-flex w-full items-center justify-center gap-2 rounded-[9px] bg-[#8B2EEA] px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-[#7A1FD6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8B2EEA] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#8B2EEA]';

const INPUT =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-[9px] text-sm text-gray-900 placeholder:text-gray-500 focus:border-[#834DFB] focus:outline-none focus:ring-[3px] focus:ring-[#834DFB]/15 aria-[invalid=true]:border-[#dc2626]';

const ERROR_TEXT: Record<Stage, string> = {
  create: 'The loan could not be created. Your entries are kept. Check your connection and try again.',
  save: 'The loan details could not be saved. Your entries are kept. Check your connection and try again.',
  upload:
    'Some documents could not be uploaded. Your entries are kept. Check your connection and try again.',
};

class UploadsFailed extends Error {}

function formatSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export const DraftIntakeBA: React.FC = () => {
  const draft = useDraftJob(JobType.BA);

  const [form, setForm] = useState<BAFormState>(emptyForm);
  const formRef = useRef(form);
  formRef.current = form;
  const [borrowerError, setBorrowerError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const [queued, setQueued] = useState<QueuedFile[]>([]);
  const queuedRef = useRef(queued);
  queuedRef.current = queued;
  const files = useRef(new Map<string, File>());
  const [rejections, setRejections] = useState<Rejection[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idCounter = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const slotRef = useRef<string | undefined>(undefined);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const patchQueued = (id: string, patch: Partial<QueuedFile>) =>
    setQueued((prev) => prev.map((q) => (q.id === id ? { ...q, ...patch } : q)));

  /**
   * Create the job (once), save the loan with everything entered, upload the
   * queued files and open the new loan's Intake.
   */
  const materialise = async () => {
    if (running.current) return;
    running.current = true;
    setBusy(true);
    setError(null);
    setSaveStatus('saving');
    let stage: Stage = 'create';
    try {
      const snapshot = formRef.current;
      const response = await draft.create(snapshot.borrowerName.trim());
      const jobId = response.job.jobId;

      stage = 'save';
      // A BA job has one loan; use the one the backend made, else create it.
      const first = await loadIntakeData(jobId);
      const loan = await ensureLoan(jobId, first.loan);
      await saveLoanFields(loan, first.tenantId, snapshot);

      stage = 'upload';
      const bucket = response.uploadBucket || import.meta.env.VITE_S3_DOCUMENTS_BUCKET;
      const jobRef = {
        jobId,
        uploadLocation: response.job.uploadLocation ?? `s3://${bucket}/${response.uploadPrefix}`,
      };
      let failed = 0;
      // No file can be added while this runs, so this list is the whole queue.
      const queue = queuedRef.current;
      const completedIds = new Set(queue.filter((q) => q.status === 'completed').map((q) => q.id));
      for (const item of queue) {
        if (completedIds.has(item.id)) continue;
        const file = files.current.get(item.id);
        if (!file || !bucket) {
          failed += 1;
          patchQueued(item.id, { status: 'error', error: 'Upload failed. Try again.' });
          continue;
        }
        patchQueued(item.id, { status: 'uploading', error: undefined });
        try {
          await uploadDocument(file, item.name, loan.loanId, jobRef, bucket);
          patchQueued(item.id, { status: 'completed' });
          completedIds.add(item.id);
        } catch (err) {
          console.error('[DraftIntakeBA] Upload failed:', err);
          failed += 1;
          patchQueued(item.id, { status: 'error', error: 'Upload failed. Try again.' });
        }
      }
      if (failed > 0) throw new UploadsFailed();

      // Edits made while this ran are saved too, so nothing typed is lost.
      stage = 'save';
      const latest = formRef.current;
      if (!formsEqual(latest, snapshot)) await saveLoanFields(loan, first.tenantId, latest);

      const done = queue.filter((q) => completedIds.has(q.id));
      const assignments: Record<string, string> = {};
      for (const q of done) if (q.slot) assignments[q.name] = q.slot;
      saveIntakeRecord(jobId, { extras: pickExtras(latest), assignments, submitted: [] });
      setDraftStagedFiles(
        jobId,
        done.map((q) => ({ id: `draft-${q.id}`, name: q.name, size: q.size, status: 'completed' }))
      );

      setSaveStatus('saved');
      draft.open(response);
    } catch (err) {
      console.error('[DraftIntakeBA] Draft action failed:', err);
      setError(err instanceof UploadsFailed ? ERROR_TEXT.upload : ERROR_TEXT[stage]);
      setSaveStatus('error');
      setBusy(false);
      running.current = false;
    }
  };

  const handleChange = (key: BAFieldKey, value: string) => {
    const next = { ...form, [key]: value };
    setForm(next);
    setSaveStatus('saving');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      const validation = validateLoanDataForm(toLoanDataFormState(formRef.current));
      setBorrowerError(validation);
      if (validation) {
        // Nothing is created while the borrower name is blank.
        setSaveStatus('idle');
        return;
      }
      void materialise();
    }, AUTOSAVE_DELAY_MS);
  };

  const handleFiles = (picked: File[], slot?: string) => {
    if (picked.length === 0 || running.current) return;
    const rejected: Rejection[] = [];
    const accepted: File[] = [];
    for (const file of picked) {
      const reason = validateIntakeFile(file);
      if (reason) rejected.push({ name: file.name, reason });
      else accepted.push(file);
    }
    setRejections(rejected);
    if (accepted.length === 0) return;

    const taken = new Set<string>(queuedRef.current.map((q) => q.name));
    const entries: QueuedFile[] = accepted.map((file) => {
      const name = uniqueFileName(file.name, taken);
      taken.add(name);
      const id = `file-${++idCounter.current}`;
      files.current.set(id, file);
      return { id, name, size: file.size, slot, status: 'queued' };
    });
    queuedRef.current = [...queuedRef.current, ...entries];
    setQueued(queuedRef.current);
    // The first file is the first real action: it creates the job.
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    void materialise();
  };

  const openPicker = (slot?: string) => {
    slotRef.current = slot;
    fileInput.current?.click();
  };

  const handlePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    const slot = slotRef.current;
    slotRef.current = undefined;
    e.target.value = '';
    handleFiles(picked, slot);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const assignments = useMemo(() => {
    const result: Record<string, string> = {};
    for (const q of queued) if (q.slot) result[q.name] = q.slot;
    return result;
  }, [queued]);
  const checklist = useMemo(
    () =>
      buildChecklist({
        fileNames: queued.filter((q) => q.status === 'completed').map((q) => q.name),
        documentTypes: [],
        assignments,
      }),
    [queued, assignments]
  );
  const missingCount = countMissing(checklist);
  const receivedCount = checklist.length - missingCount;

  const waiting = queued.filter((q) => q.status !== 'completed').length;
  const action = getExtractAction({
    pendingCount: waiting,
    submittedCount: 0,
    missingCount,
    alreadyExtracting: false,
    uploading: true,
  });

  const statusText = saveStatus === 'idle' ? 'Saves as you type' : saveStatusLabel(saveStatus);

  return (
    <div
      data-testid="intake-ba"
      className="grid max-w-[1200px] grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] items-start gap-4"
    >
      {/* Loan information */}
      <section aria-labelledby="ba-loan-info" className={`${CARD} flex flex-col gap-4 p-[18px]`}>
        <div className="flex items-center justify-between gap-3">
          <h2 id="ba-loan-info" className="text-base font-bold text-gray-900">
            Loan information
          </h2>
          <span
            data-testid="save-status"
            aria-live="polite"
            className={`text-xs font-bold ${
              saveStatus === 'saved'
                ? 'text-[#15803d]'
                : saveStatus === 'error'
                  ? 'text-[#991b1b]'
                  : 'text-gray-600'
            }`}
          >
            {statusText}
          </span>
        </div>

        {BA_FIELD_GROUPS.map((group) => (
          <div key={group.name} role="group" aria-label={group.name}>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {group.name}
            </h3>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
              {group.fields.map((field) => {
                const id = `ba-${field.key}`;
                const invalid = field.key === 'borrowerName' && !!borrowerError;
                return (
                  <div key={field.key} className="flex min-w-0 flex-col gap-[5px]">
                    <label htmlFor={id} className="text-[13px] font-bold text-gray-700">
                      {field.label}
                      {field.key === 'borrowerName' && (
                        <span className="ml-0.5 text-[#dc2626]" aria-hidden="true">
                          *
                        </span>
                      )}
                    </label>
                    <input
                      id={id}
                      type="text"
                      value={form[field.key]}
                      placeholder={field.placeholder}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      aria-required={field.key === 'borrowerName' ? true : undefined}
                      aria-invalid={invalid || undefined}
                      aria-describedby={invalid ? `${id}-error` : undefined}
                      className={INPUT}
                    />
                    {invalid && (
                      <p id={`${id}-error`} role="alert" className="text-xs text-[#991b1b]">
                        {borrowerError}. Nothing is saved until it is filled in.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* Documents */}
      <div className="flex min-w-0 flex-col gap-4">
        <input
          ref={fileInput}
          type="file"
          multiple
          accept={ACCEPT_ATTRIBUTE}
          onChange={handlePicked}
          className="hidden"
          tabIndex={-1}
          aria-label="Choose documents"
          data-testid="file-input"
        />

        <div
          data-testid="dropzone"
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`rounded-xl border-2 border-dashed p-[26px] text-center transition-colors ${
            dragOver ? 'border-[#834DFB] bg-[#F5F3FF]' : 'border-[#DDD0FF] bg-white'
          }`}
        >
          <p className="text-base font-bold text-gray-900">Drop the loan&apos;s documents</p>
          <p className="mt-1 text-[13px] text-gray-600">
            {ACCEPTED_FORMATS_TEXT} · up to 10 MB each
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => openPicker()}
            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-[#DDD0FF] bg-white px-3 py-1.5 text-sm font-bold text-[#5A21C9] hover:bg-[#F5F3FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Upload size={14} strokeWidth={2} aria-hidden="true" />
            Choose files
          </button>
        </div>

        {rejections.length > 0 && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800">
            <p className="font-bold">
              {rejections.length === 1 ? '1 file was not added' : `${rejections.length} files were not added`}
            </p>
            <ul className="mt-1 list-disc pl-5">
              {rejections.map((r, i) => (
                <li key={`${r.name}-${i}`}>
                  <span className="font-bold">{r.name}</span>: {r.reason}
                </li>
              ))}
            </ul>
          </div>
        )}

        {queued.length > 0 && (
          <ul data-testid="staged-files" aria-label="Files added" className={`${CARD} divide-y divide-gray-100`}>
            {queued.map((q) => (
              <li key={q.id} className="flex items-center gap-2.5 px-3.5 py-2 text-sm">
                <FileText size={14} strokeWidth={2} className="shrink-0 text-gray-500" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate text-gray-700" title={q.name}>
                  {q.name}
                </span>
                <span className="shrink-0 text-xs text-gray-600">{formatSize(q.size)}</span>
                {(q.status === 'uploading' || q.status === 'queued') && (
                  <span className="inline-flex shrink-0 items-center gap-1 text-xs font-bold text-gray-600">
                    <Loader size={12} strokeWidth={2} className="animate-spin" aria-hidden="true" />
                    {q.status === 'queued' ? 'Waiting' : 'Uploading'}
                  </span>
                )}
                {q.status === 'completed' && (
                  <span className="shrink-0 text-xs font-bold text-[#15803d]">Ready</span>
                )}
                {q.status === 'error' && (
                  <span className="shrink-0 text-xs font-bold text-[#991b1b]">{q.error}</span>
                )}
              </li>
            ))}
          </ul>
        )}

        <section
          data-testid="checklist"
          aria-labelledby="ba-checklist"
          className={`${CARD} overflow-hidden`}
        >
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-[#f9fafb] px-3.5 py-2.5">
            <h2 id="ba-checklist" className="text-[13px] font-bold text-gray-700">
              Standard borrower documents
            </h2>
            <span className="text-xs text-gray-600">
              {receivedCount} of {checklist.length} received
            </span>
          </div>
          <ul>
            {checklist.map((row) => (
              <li
                key={row.id}
                data-testid={`checklist-${row.id}`}
                data-received={row.received}
                className="flex items-center gap-2.5 border-b border-[#f9fafb] px-3.5 py-[9px] last:border-b-0"
              >
                <StatusDot
                  tone={row.received ? 'success' : 'idle'}
                  size={10}
                  label={row.received ? 'Received' : 'Missing'}
                />
                <span className="min-w-0 flex-1 text-sm text-gray-700">{row.label}</span>
                {row.received ? (
                  <span className="text-xs font-bold text-[#15803d]">Received</span>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => openPicker(row.id)}
                    aria-label={`Upload ${row.label}`}
                    className="rounded text-xs font-bold text-[#6E2EF0] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Upload
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-red-200 bg-[#fee2e2] px-3 py-2 text-sm text-[#991b1b]"
          >
            <AlertCircle size={16} strokeWidth={2} className="mt-0.5 shrink-0" aria-hidden="true" />
            <p className="flex-1">{error}</p>
            <button
              type="button"
              disabled={busy}
              onClick={() => void materialise()}
              className="shrink-0 rounded font-bold underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#991b1b] disabled:opacity-50"
            >
              Retry
            </button>
          </div>
        )}

        <button
          type="button"
          data-testid="extract-action"
          disabled
          title={waiting > 0 ? action.title : 'Add at least one document to extract.'}
          className={EXTRACT_BUTTON}
        >
          {action.label}
        </button>
      </div>
    </div>
  );
};

export default DraftIntakeBA;
